import type { CampaignOperations, CampaignOperationsExpectedValues, CampaignOperationsFields, CampaignVisibility } from "./campaignOperations";

export enum InfluencerCampaignStatus {
  NotStarted = "Not started",
  InProgress = "In progress",
  Backlog = "Backlog",
  Completed = "Completed",
}

export enum InfluencerCampaignTrafficLight {
  Red = "red",
  Yellow = "yellow",
  Green = "green",
}

export enum CampaignLimitationKind {
  Region = "region",
}

export enum InfluencerCampaignType {
  ProductReview = "product_review",
  UgcReview = "ugc_review",
  Affiliate = "affiliate",
  Sampling = "sampling",
  Giveaway = "giveaway",
}

export const INFLUENCER_CAMPAIGN_TYPE_LABEL: Record<InfluencerCampaignType, string> = {
  [InfluencerCampaignType.ProductReview]: "Product Review",
  [InfluencerCampaignType.UgcReview]: "UGC Review",
  [InfluencerCampaignType.Affiliate]: "Affiliate",
  [InfluencerCampaignType.Sampling]: "Product Sampling",
  [InfluencerCampaignType.Giveaway]: "Giveaway",
};

// Per-campaign-type configuration, discriminated by `type` (mirrors Facade). Giveaway is the only variant today.
export interface GiveawayCampaignConfig {
  type: InfluencerCampaignType.Giveaway;
  openGlobally?: boolean;
  requireContentForReward?: boolean;
}

export type CampaignTypeConfig = GiveawayCampaignConfig;

export enum InfluencerCampaignPlatform {
  Tiktok = "tiktok",
  Instagram = "instagram",
  Youtube = "youtube",
  X = "x",
  Facebook = "facebook",
  Threads = "threads",
}

export const INFLUENCER_CAMPAIGN_PLATFORM_LABEL: Record<InfluencerCampaignPlatform, string> = {
  [InfluencerCampaignPlatform.Tiktok]: "TikTok",
  [InfluencerCampaignPlatform.Instagram]: "Instagram",
  [InfluencerCampaignPlatform.Youtube]: "YouTube",
  [InfluencerCampaignPlatform.X]: "X",
  [InfluencerCampaignPlatform.Facebook]: "Facebook",
  [InfluencerCampaignPlatform.Threads]: "Threads",
};

/** Standard campaigns recruit creators who post content, so they keep the two content platforms. */
export const STANDARD_CAMPAIGN_PLATFORMS: InfluencerCampaignPlatform[] = [
  InfluencerCampaignPlatform.Tiktok,
  InfluencerCampaignPlatform.Instagram,
];

/** Affiliate programmes are shared as a link anywhere, so they list the six referral channels. */
export const AFFILIATE_CAMPAIGN_PLATFORMS: InfluencerCampaignPlatform[] = Object.values(InfluencerCampaignPlatform);

export enum CampaignRewardBadge {
  Gifted = "gifted",
  Paid = "paid",
  Commission = "commission",
  Bonus = "bonus",
  Boost = "boost",
}

export const CAMPAIGN_REWARD_BADGE_LABEL: Record<CampaignRewardBadge, string> = {
  [CampaignRewardBadge.Gifted]: "Gifted",
  [CampaignRewardBadge.Paid]: "Fixed pay",
  [CampaignRewardBadge.Commission]: "Commission",
  [CampaignRewardBadge.Bonus]: "Bonus Eligible",
  [CampaignRewardBadge.Boost]: "Boost Eligible",
};

export interface CampaignReward {
  fixed?: number;
  commission?: number;
  bonus?: number;
  productWorth?: number;
  badges?: CampaignRewardBadge[];
  // Creator-choice: creator picks this many SKUs from productAllocations at apply time. Unset = fixed (receive all).
  productChoiceCount?: number;
}

export interface CampaignLimitation {
  kind: CampaignLimitationKind;
  values: string[];
}

export interface ProductAllocation {
  sku: string;
  quantity: number;
  value?: number;
  groupId?: string;
  disabled?: boolean;
}

export interface ProductGroup {
  groupId: string;
  name?: string;
  choiceCount: number;
}

export interface CampaignBriefDeliverable {
  type: string;
  count?: number;
  description?: string;
}

export interface CampaignBriefTimelineEntry {
  label: string;
  date?: string;
}

export interface CampaignBriefFaqEntry {
  question: string;
  answer: string;
}

export interface CampaignBriefPastResult {
  label: string;
  metric?: string;
}

export interface CampaignBriefEligibility {
  minFollowers?: number;
  minTier?: number;
}

// Mission brief → Freedom / Usage / Scope shown on the hub campaign detail.
export interface CampaignBriefUsageRights {
  freedom: string;
  usagePeriod: string;
  scope: string;
}

export interface CampaignBriefStructured {
  coreMessage?: string;
  images?: string[];
  requiredHashtags?: string[];
  prohibited?: string[];
  deliverables?: CampaignBriefDeliverable[];
  timeline?: CampaignBriefTimelineEntry[];
  faq?: CampaignBriefFaqEntry[];
  pastResults?: CampaignBriefPastResult[];
  eligibility?: CampaignBriefEligibility;
  brandLookFor?: string[];
  usageRights?: CampaignBriefUsageRights;
}

// An affiliate campaign's customer discount and commission rates; an absent field uses the programme default.
export interface AffiliateCampaignTerms {
  discountPercent?: number;
  couponPercent?: number;
  utmPercent?: number;
  couponUtmPercent?: number;
}

export interface InfluencerCampaign {
  campaignId: string;
  name: string;
  brandId: string;
  status: InfluencerCampaignStatus;
  ownerId: string;
  // Absent = standard; fixed at creation (the backend 409s on a change).
  pipelineType?: string;
  // Affiliate campaigns only.
  affiliateTerms?: AffiliateCampaignTerms;
  onboardingTarget?: number;
  postTarget?: number;
  postingDueDate?: string;
  brief?: string;
  briefStructured?: CampaignBriefStructured;
  consideration?: string;
  trafficLight?: InfluencerCampaignTrafficLight;
  productRefs?: string[];
  productAllocations?: ProductAllocation[];
  productGroups?: ProductGroup[];
  limitations?: CampaignLimitation[];
  types?: InfluencerCampaignType[];
  typeConfig?: CampaignTypeConfig;
  platforms?: InfluencerCampaignPlatform[];
  tagline?: string;
  reward?: CampaignReward;
  startAt?: string;
  endAt?: string;
  // Admin-assigned manual order (lower = earlier); undefined for campaigns never reordered, which sort by recency after the ordered ones.
  sortOrder?: number;
  // Influencer team tracker fields (replaces the master sheet); history is server-written.
  operations?: CampaignOperations;
  // Absent = public; INTERNAL campaigns never appear in Creator Hub.
  visibility?: CampaignVisibility;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  // Live participant actuals from the list endpoint (undefined on single-campaign GET; dashboard cards/table/KPIs read these).
  onboardedCount?: number;
  postedCount?: number;
}

export interface InfluencerCampaignFilter {
  ownerId?: string;
  status?: InfluencerCampaignStatus;
  brandId?: string;
}

export interface CreateInfluencerCampaignRequest {
  name: string;
  brandId: string;
  status: InfluencerCampaignStatus;
  ownerId: string;
  // Only accepted on create; changing it afterwards is a 409.
  pipelineType?: string;
  // Affiliate campaigns only.
  affiliateTerms?: AffiliateCampaignTerms;
  onboardingTarget?: number;
  postTarget?: number;
  postingDueDate?: string;
  brief?: string;
  briefStructured?: CampaignBriefStructured;
  consideration?: string;
  trafficLight?: InfluencerCampaignTrafficLight;
  productRefs?: string[];
  productAllocations?: ProductAllocation[];
  productGroups?: ProductGroup[];
  limitations?: CampaignLimitation[];
  types?: InfluencerCampaignType[];
  typeConfig?: CampaignTypeConfig;
  platforms?: InfluencerCampaignPlatform[];
  tagline?: string;
  reward?: CampaignReward;
  startAt?: string;
  endAt?: string;
  visibility?: CampaignVisibility;
}

export interface UpdateInfluencerCampaignRequest
  extends Partial<Omit<CreateInfluencerCampaignRequest, "status" | "pipelineType" | "affiliateTerms">> {
  // Replaces the stored terms; null clears them back to the programme defaults.
  affiliateTerms?: AffiliateCampaignTerms | null;
  // Partial tracker patch merged server-side; the editor is recorded in history.
  operations?: CampaignOperationsFields;
  operationsEditor?: string;
  // Field-level optimistic lock: the values the editor saw before this change.
  operationsExpected?: CampaignOperationsExpectedValues;
}

export type CampaignImageContentType = "image/png" | "image/jpeg" | "image/webp";

export interface CreateCampaignImagePresignedUrlRequest {
  fileName: string;
  contentType: CampaignImageContentType;
  sizeBytes: number;
}

export interface CreateCampaignImagePresignedUrlResponse {
  presignedUrl: string;
  objectKey: string;
  cdnUrl: string;
}

export const CAMPAIGN_LIMITATION_KIND_LABEL: Record<CampaignLimitationKind, string> = {
  [CampaignLimitationKind.Region]: "Region",
};

export const CAMPAIGN_REGION_OPTIONS: Array<{ code: string; label: string }> = [
  { code: "US", label: "United States" },
  { code: "JP", label: "Japan" },
  { code: "KR", label: "South Korea" },
  { code: "TW", label: "Taiwan" },
  { code: "SG", label: "Singapore" },
  { code: "MY", label: "Malaysia" },
  { code: "VN", label: "Vietnam" },
  { code: "TH", label: "Thailand" },
  { code: "ID", label: "Indonesia" },
  { code: "PH", label: "Philippines" },
  { code: "AU", label: "Australia" },
  { code: "CA", label: "Canada" },
  { code: "GB", label: "United Kingdom" },
  // SSD-5423 — expanded to every market SkinSeoul ships to (global campaigns).
  // Keep in sync with CreatorHub packages/service campaignRegions.ts.
  { code: "NZ", label: "New Zealand" },
  { code: "AE", label: "United Arab Emirates" },
  { code: "SA", label: "Saudi Arabia" },
  { code: "QA", label: "Qatar" },
  { code: "NL", label: "Netherlands" },
  { code: "BE", label: "Belgium" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "PL", label: "Poland" },
  { code: "KZ", label: "Kazakhstan" },
  { code: "MN", label: "Mongolia" },
  { code: "AZ", label: "Azerbaijan" },
  { code: "CH", label: "Switzerland" },
  { code: "DK", label: "Denmark" },
  { code: "NO", label: "Norway" },
  { code: "CO", label: "Colombia" },
  { code: "CL", label: "Chile" },
  { code: "MX", label: "Mexico" },
  { code: "TR", label: "Turkey" },
  // SSD-6493 — Southern Europe markets requested by marketing.
  { code: "IT", label: "Italy" },
  { code: "ES", label: "Spain" },
];

// The complete manually ordered block; campaigns left out are cleared back to creation-date ordering.
export interface ReorderInfluencerCampaignsRequest {
  orderedCampaignIds: string[];
}

export interface UpdateInfluencerCampaignStatusRequest {
  status: InfluencerCampaignStatus;
}

export const STATUS_ORDER: InfluencerCampaignStatus[] = [
  InfluencerCampaignStatus.NotStarted,
  InfluencerCampaignStatus.Backlog,
  InfluencerCampaignStatus.InProgress,
  InfluencerCampaignStatus.Completed,
];

export const STATUS_META: Record<
  InfluencerCampaignStatus,
  { label: string; tone: string; pill: string }
> = {
  [InfluencerCampaignStatus.NotStarted]: {
    label: "Not started",
    tone: "tw-text-muted-foreground",
    pill: "tw-bg-muted tw-text-muted-foreground",
  },
  [InfluencerCampaignStatus.Backlog]: {
    label: "Backlog",
    tone: "tw-text-amber-700",
    pill: "tw-bg-amber-100 tw-text-amber-700",
  },
  [InfluencerCampaignStatus.InProgress]: {
    label: "In progress",
    tone: "tw-text-blue-700",
    pill: "tw-bg-blue-100 tw-text-blue-700",
  },
  [InfluencerCampaignStatus.Completed]: {
    label: "Completed",
    tone: "tw-text-green-700",
    pill: "tw-bg-green-100 tw-text-green-700",
  },
};

export const TRAFFIC_DOT: Record<InfluencerCampaignTrafficLight, string> = {
  [InfluencerCampaignTrafficLight.Red]: "tw-bg-red-500",
  [InfluencerCampaignTrafficLight.Yellow]: "tw-bg-amber-500",
  [InfluencerCampaignTrafficLight.Green]: "tw-bg-emerald-500",
};
